// Scholars GraphQL operation documents (staff UI).
// U2-CFG removed `term` from all term-scoped operations — term is now resolved server-side.
// U4-CFG added getAdmissionsCycles, startHonorsCycle, startScholarsCycle.

// --- Program Configuration (B1–B4, A2) — term resolved server-side -----------
export const getScholarsPrograms = `
    query getScholarsPrograms {
        getScholarsPrograms {
            subProgramId
            term
            label
            communicationCode
            capacity
            capacityUsed
            isOpen
            automaticCloseDatetime
            pdnyCriteriaEnabled
            globalPdnyEnabled
            programManagerEmployeeId
            viewerRole
            newCount
            inProgressCount
            pastDueCount
            admitAwaitingPublishCount
            admitPublishedCount
        }
    }
`;

export const updateScholarsProgram = `
    mutation updateScholarsProgram($subProgramId: String!, $label: String, $automaticCloseDatetime: String) {
        updateScholarsProgram(subProgramId: $subProgramId, label: $label, automaticCloseDatetime: $automaticCloseDatetime) { status }
    }
`;

export const setScholarsProgramCapacity = `
    mutation setScholarsProgramCapacity($subProgramId: String!, $capacity: Int!) {
        setScholarsProgramCapacity(subProgramId: $subProgramId, capacity: $capacity) { status }
    }
`;

export const openCloseScholarsProgram = `
    mutation openCloseScholarsProgram($subProgramId: String!, $action: String!) {
        openCloseScholarsProgram(subProgramId: $subProgramId, action: $action) { status }
    }
`;

export const setScholarsProgramPdny = `
    mutation setScholarsProgramPdny($subProgramId: String!, $enabled: Boolean!, $criteriaRules: AWSJSON) {
        setScholarsProgramPdny(subProgramId: $subProgramId, enabled: $enabled, criteriaRules: $criteriaRules) { status }
    }
`;

export const getScholarsGlobalPdny = `
    query getScholarsGlobalPdny {
        getScholarsGlobalPdny {
            term
            enabled
            criteriaRules
            updatedBy
            updatedDatetime
        }
    }
`;

export const setScholarsGlobalPdny = `
    mutation setScholarsGlobalPdny($enabled: Boolean!, $criteriaRules: AWSJSON) {
        setScholarsGlobalPdny(enabled: $enabled, criteriaRules: $criteriaRules) { status }
    }
`;

// has570 — global Scholars review period (reviewer complete-by window, in days).
export const getScholarsReviewPeriodDays = `
    query getScholarsReviewPeriodDays {
        getScholarsReviewPeriodDays
    }
`;

export const setScholarsReviewPeriodDays = `
    mutation setScholarsReviewPeriodDays($days: Int!) {
        setScholarsReviewPeriodDays(days: $days) { status }
    }
`;

export const getScholarsProgram = `
    query getScholarsProgram($subProgramId: String!) {
        getScholarsProgram(subProgramId: $subProgramId) {
            subProgramId
            term
            label
            pdnyCriteriaEnabled
            pdnyCriteriaRules
            globalPdnyEnabled
        }
    }
`;

// --- Common Queue + Pull-Next (E2/E4/F3) — term resolved server-side ---------
export const getScholarsProgramQueue = `
    query getScholarsProgramQueue($subProgramId: String!) {
        getScholarsProgramQueue(subProgramId: $subProgramId) {
            subProgramId
            term
            capacity
            capacityUsed
            isOpen
            entries {
                applicantEmployeeId
                programId
                subProgramId
                term
                priority
                createdDate
                status
                reviewerEmployeeId
                recommendation
                type
            }
        }
    }
`;

// has-inprogress — the caller's own claimed (IN_REVIEW/RECOMMENDED) applicants across all programs,
// for the Common Queue "In Progress" grid. Ownership-scoped + field-projected server-side; term server-resolved.
export const getScholarsReviewerInProgress = `
    query getScholarsReviewerInProgress {
        getScholarsReviewerInProgress {
            limit
            entries {
                applicantEmployeeId
                firstName
                lastName
                programId
                subProgramId
                term
                priority
                createdDate
                status
                manuallyAssigned
                manuallyAssignedProgram
                prioritized
                resumeLater
                completedByDueDate
                recommendation
                recommendedDatetime
                type
            }
        }
    }
`;

export const pullNextScholarsApplicant = `
    mutation pullNextScholarsApplicant($subProgramId: String!) {
        pullNextScholarsApplicant(subProgramId: $subProgramId) {
            item { applicantEmployeeId programId subProgramId term status }
            reason
        }
    }
`;

// --- "Initiate Preferences" run (C1) — term resolved server-side -------------
export const triggerScholarsPreferencingRun = `
    mutation triggerScholarsPreferencingRun($filterModel: AWSJSON) {
        triggerScholarsPreferencingRun(filterModel: $filterModel) {
            executionId
            started
            message
        }
    }
`;

// --- Recommendation (E3, recommend→approve) — term optional (validated) ------
// has600 — administrativeNotes carries the reviewer's checked admin-note selections so they persist as a
// ReviewNotes entity and appear in the Admin Notes report (parity with the Honors submit flow).
export const submitScholarsRecommendation = `
    mutation submitScholarsRecommendation($applicantEmployeeId: String!, $subProgramId: String!, $recommendation: String!, $rubricValues: AWSJSON, $administrativeNotes: AWSJSON) {
        submitScholarsRecommendation(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId, recommendation: $recommendation, rubricValues: $rubricValues, administrativeNotes: $administrativeNotes) { status }
    }
`;

// --- Assign (Actions menu) — admin manually assigns an applicant into a program's queue (owning PM
// In Progress grid). Term optional (derived server-side from the applicant's record). ------
export const assignApplicationToProgram = `
    mutation assignApplicationToProgram($applicantEmployeeId: String!, $subProgramId: String!) {
        assignApplicationToProgram(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId) { status }
    }
`;

// --- resume-later (Actions menu label: "Resume Later") — flag the caller's own in-progress placement
// (pulled or manually assigned to them) as resumeLater. Term optional (derived server-side). reason=AT_LIMIT
// when the caller is at the per-scholars program cap. ------
export const resumeLaterScholarsApplicant = `
    mutation resumeLaterScholarsApplicant($applicantEmployeeId: String!, $subProgramId: String!, $administrativeNotes: [AdministrativeNoteInput]) {
        resumeLaterScholarsApplicant(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId, administrativeNotes: $administrativeNotes) { status reason }
    }
`;

// --- release-resume-later (Review Cascade grid row action: "Release from Saved for Later") — Scholars ADMIN
// clears a reviewer's resumeLater flag on an applicant's active queue placement. Term optional (derived
// server-side). reason=NOT_SAVED_FOR_LATER when the placement is not currently saved for later. ------
export const releaseScholarsResumeLater = `
    mutation releaseScholarsResumeLater($applicantEmployeeId: String!, $subProgramId: String!, $term: String) {
        releaseScholarsResumeLater(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId, term: $term) { status reason }
    }
`;

export const recordScholarsDecision = `
    mutation recordScholarsDecision($applicantEmployeeId: String!, $subProgramId: String!, $decision: String!) {
        recordScholarsDecision(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId, decision: $decision) {
            decision status subProgramId workflowId
        }
    }
`;

// has615 — ADMIN bulk-lock for the Recommendations to Approve "Approve" action. Sets isPendingProcessing =
// true on the selected applicants so the processing icon shows while the dialog's synchronous approval loop
// runs. term resolved server-side when omitted.
export const lockScholarsPendingProcessing = `
    mutation lockScholarsPendingProcessing($applicantEmployeeIds: [String!]!, $lock: Boolean) {
        lockScholarsPendingProcessing(applicantEmployeeIds: $applicantEmployeeIds, lock: $lock) {
            locked
        }
    }
`;

// --- Release to review (E1) — term resolved server-side ----------------------
export const releaseScholarsToReview = `
    mutation releaseScholarsToReview($releaseScope: AWSJSON) {
        releaseScholarsToReview(releaseScope: $releaseScope) { status }
    }
`;

// --- Reviewer access (F1/F2) + Manage Reviewers (U1-MSR) — term resolved server-side --------------
export const listScholarsReviewers = `
    query listScholarsReviewers($subProgramId: String!, $includeDisabled: Boolean) {
        listScholarsReviewers(subProgramId: $subProgramId, includeDisabled: $includeDisabled) {
            reviewerEmployeeId subProgramId term status firstName lastName quota submittedCount
        }
    }
`;

export const listScholarsReviewerCandidates = `
    query listScholarsReviewerCandidates($subProgramId: String) {
        listScholarsReviewerCandidates(subProgramId: $subProgramId) {
            employeeId firstName lastName alreadyGranted
        }
    }
`;

export const getScholarsReviewerQuota = `
    query getScholarsReviewerQuota($subProgramId: String!) {
        getScholarsReviewerQuota(subProgramId: $subProgramId) {
            subProgramId quota submittedCount
        }
    }
`;

export const setScholarsReviewerQuota = `
    mutation setScholarsReviewerQuota($reviewerEmployeeId: String!, $subProgramId: String!, $quota: Int!) {
        setScholarsReviewerQuota(reviewerEmployeeId: $reviewerEmployeeId, subProgramId: $subProgramId, quota: $quota) { status }
    }
`;

export const grantScholarsReviewerAccess = `
    mutation grantScholarsReviewerAccess($reviewerEmployeeId: String!, $subProgramId: String!) {
        grantScholarsReviewerAccess(reviewerEmployeeId: $reviewerEmployeeId, subProgramId: $subProgramId) { status }
    }
`;

export const revokeScholarsReviewerAccess = `
    mutation revokeScholarsReviewerAccess($reviewerEmployeeId: String!, $subProgramId: String!) {
        revokeScholarsReviewerAccess(reviewerEmployeeId: $reviewerEmployeeId, subProgramId: $subProgramId) { status }
    }
`;

// --- Alternate assignment (G7) — term resolved server-side -------------------
export const getScholarsAlternateList = `
    query getScholarsAlternateList {
        getScholarsAlternateList {
            items { applicantEmployeeId firstName lastName term priority createdDate alternateOptIn scholarsOutcomeStatus }
        }
    }
`;

export const admitScholarsFromAlternate = `
    mutation admitScholarsFromAlternate($applicantEmployeeId: String!, $subProgramId: String!) {
        admitScholarsFromAlternate(applicantEmployeeId: $applicantEmployeeId, subProgramId: $subProgramId) {
            admitted applicantEmployeeId subProgramId term
        }
    }
`;

// has552 — ADMIN bulk-deny of opt-in applicants from the Opt-In List (term resolved server-side). Parks each
// applicant pending final-deny on Recommendations to Approve; an admin finalizes there via finalizeScholarsDeny.
export const bulkDenyScholarsFromAlternate = `
    mutation bulkDenyScholarsFromAlternate($applicantEmployeeIds: [String!]!) {
        bulkDenyScholarsFromAlternate(applicantEmployeeIds: $applicantEmployeeIds) {
            denied skipped failed { applicantEmployeeId reason }
        }
    }
`;

// has552 — ADMIN finalizes a DENY for an applicant pending final-deny on Recommendations to Approve
// (term derived from the applicant record). Completes the workflow if present, sets SCH_FINAL_DENY.
export const finalizeScholarsDeny = `
    mutation finalizeScholarsDeny($applicantEmployeeId: String!) {
        finalizeScholarsDeny(applicantEmployeeId: $applicantEmployeeId) {
            decision status subProgramId workflowId
        }
    }
`;

// has572 — Advanced Search bulk admin recommendation (Scholars). Both start an async state machine and
// return a ProcessResponse (executionId). Term is resolved server-side; criteria is the grid's search input.
// bulkAdminDenyScholars: denies the searched population (all three scenarios) with a chosen reason.
export const bulkAdminDenyScholars = `
    mutation bulkAdminDenyScholars($input: BulkAdminDenyScholarsInput!) {
        bulkAdminDenyScholars(input: $input) {
            status executionId message
        }
    }
`;

// bulkAdminAdmitScholars: admits the searched population into ONE admin-chosen sub-program (reason is the
// fixed "Admin Recommendation", set server-side).
export const bulkAdminAdmitScholars = `
    mutation bulkAdminAdmitScholars($input: BulkAdminAdmitScholarsInput!) {
        bulkAdminAdmitScholars(input: $input) {
            status executionId message
        }
    }
`;

// Single-applicant administrative recommendation (Scholars applicant-detail Actions menu). ADMIN-only; the
// SAME admin recommendation as the bulk action (bulkAdminAdmit/Deny) scoped to ONE applicant and run
// synchronously. decision is "ADMIT" | "DENY"; subProgramId is required for ADMIT; reason for DENY. term
// resolved server-side.
export const adminRecommendScholar = `
    mutation adminRecommendScholar($applicantEmployeeId: String!, $decision: String!, $subProgramId: String, $reason: String) {
        adminRecommendScholar(applicantEmployeeId: $applicantEmployeeId, decision: $decision, subProgramId: $subProgramId, reason: $reason) {
            decision status applied subProgramId skippedReason
        }
    }
`;

// --- Publishing (H2/H3) — term resolved server-side --------------------------
export const triggerScholarsPublish = `
    mutation triggerScholarsPublish($filterModel: AWSJSON) {
        triggerScholarsPublish(filterModel: $filterModel) { executionId started }
    }
`;

// --- Admissions Cycles Configuration (U3-CFG/U4-CFG) -------------------------
export const getAdmissionsCycles = `
    query getAdmissionsCycles($programId: String!) {
        getAdmissionsCycles(programId: $programId) {
            current { cycleKey terms yearStart yearEnd datetimeCreated type }
            currentTerm
            archived { cycleKey terms yearStart yearEnd datetimeCreated datetimeArchived type }
        }
    }
`;

export const startHonorsCycle = `
    mutation startHonorsCycle($cycleKey: String!, $terms: [String!]!, $yearStart: Int!, $yearEnd: Int!) {
        startHonorsCycle(cycleKey: $cycleKey, terms: $terms, yearStart: $yearStart, yearEnd: $yearEnd) { status }
    }
`;

export const startScholarsCycle = `
    mutation startScholarsCycle($cycleKey: String!, $terms: [String!]!, $yearStart: Int!, $yearEnd: Int!) {
        startScholarsCycle(cycleKey: $cycleKey, terms: $terms, yearStart: $yearStart, yearEnd: $yearEnd) { status }
    }
`;

// --- Preferences (U4 — read) — term optional (derived from applicant record) ---
export const getScholarsPreferences = `
    query getScholarsPreferences($applicantEmployeeId: String!, $term: String) {
        getScholarsPreferences(applicantEmployeeId: $applicantEmployeeId, term: $term) {
            applicantEmployeeId
            programId
            term
            subProgramId
            rank
            status
            updatedBy
            updatedDatetime
            createdDatetime
            alternateOptIn
        }
    }
`;

// has504 — Per-applicant queue entries for cascade state display.
export const getScholarsApplicantQueueEntries = `
    query getScholarsApplicantQueueEntries($applicantEmployeeId: String!, $term: String) {
        getScholarsApplicantQueueEntries(applicantEmployeeId: $applicantEmployeeId, term: $term) {
            subProgramId
            status
            rank
            recommendation
            decisionBy
            decisionDate
            reviewerEmployeeId
            rubricValues
            updatedDatetime
            fromAlternateList
            admitSource
            administrativeNotes {
                reviewerEmployeeId
                reviewerFirstName
                reviewerLastName
                notesDocument
                notesEvaluation
                notesOthersConcerns
                submissionDatetime
            }
        }
    }
`;

// has-history — Admin-only full audit-trail history for an applicant. Every recorded action with
// actor, timestamp and raw before/after state (AWSJSON strings), oldest-first. term optional.
export const getScholarsApplicantHistory = `
    query getScholarsApplicantHistory($applicantEmployeeId: String!, $term: String) {
        getScholarsApplicantHistory(applicantEmployeeId: $applicantEmployeeId, term: $term) {
            actor
            action
            auditedSk
            createdDatetime
            before
            after
        }
    }
`;

// Review Cascade admin overview — every applicant released into the cascade for the term, with their
// per-ranked-choice queue status and current active placement. Term resolved server-side (optional).
export const getScholarsCascadeOverview = `
    query getScholarsCascadeOverview($term: String) {
        getScholarsCascadeOverview(term: $term) {
            applicantEmployeeId
            firstName
            lastName
            priority
            outcomeStatus
            stage
            activeSubProgramId
            activeStatus
            preferences {
                rank
                subProgramId
                label
                status
                recommendation
                isActive
            }
        }
    }
`;

// Review Cascade "move to next queue" — force-advance an applicant past their current active placement
// (DENY it, cascade advances to the next ranked choice). ADMIN-only; term resolved server-side.
export const advanceScholarsCascade = `
    mutation advanceScholarsCascade($applicantEmployeeId: String!, $term: String) {
        advanceScholarsCascade(applicantEmployeeId: $applicantEmployeeId, term: $term) {
            advanced
            applicantEmployeeId
            fromSubProgramId
            status
        }
    }
`;

// Review Cascade BULK "move to next queue" — advance a selected set of applicants at once (from the MRT
// row selection). ADMIN-only; term resolved server-side.
export const bulkAdvanceScholarsCascade = `
    mutation bulkAdvanceScholarsCascade($applicantEmployeeIds: [String!]!, $term: String) {
        bulkAdvanceScholarsCascade(applicantEmployeeIds: $applicantEmployeeIds, term: $term) {
            advanced
            skipped
            failed { applicantEmployeeId reason }
        }
    }
`;

// has504 — Admin adjust preferences (cascade-aware).
export const adjustScholarsPreferences = `
    mutation adjustScholarsPreferences($applicantEmployeeId: String!, $term: String, $rankings: [ScholarsPreferenceInput!]!, $alternateOptIn: Boolean!, $confirmUpstream: Boolean) {
        adjustScholarsPreferences(applicantEmployeeId: $applicantEmployeeId, term: $term, rankings: $rankings, alternateOptIn: $alternateOptIn, confirmUpstream: $confirmUpstream) {
            success
            scenario
            cascadeReset
            warning
            activeSubProgramId
        }
    }
`;

// --- Preference Change Request (student employee or admin submits proposed preference changes) ---
export const submitPreferenceChangeRequest = `
    mutation submitPreferenceChangeRequest($input: PreferenceChangeRequestInput!) {
        submitPreferenceChangeRequest(input: $input) {
            status
            successDetails
            errorDetails
        }
    }
`;

// --- Approve Preference Change Request (admin reviews, adjusts, and applies) ---
export const approvePreferenceChangeRequest = `
    mutation approvePreferenceChangeRequest($input: ApprovePreferenceChangeRequestInput!) {
        approvePreferenceChangeRequest(input: $input) {
            status
            successDetails
            errorDetails
        }
    }
`;

// --- Process Scholars Request (SINGLE entry point: appeal approve/deny; program change/withdraw) ---
// requestType "APPEAL" with isApproved (+ subProgramId when approving — one of the applicant's ranked
// preferences); "PROGRAMCHANGE"/"WITHDRAW" cancel the application. Separate from the Honors
// approveDenyRequests / studentRequestProcessed mutations (FR-22).
export const processScholarsRequest = `
    mutation processScholarsRequest($applicantEmployeeIds: [String!]!, $term: String, $requestType: String!, $isApproved: Boolean, $subProgramId: String) {
        processScholarsRequest(applicantEmployeeIds: $applicantEmployeeIds, term: $term, requestType: $requestType, isApproved: $isApproved, subProgramId: $subProgramId) {
            status
            successDetails
            errorDetails
        }
    }
`;

// --- Create Scholars Request (SINGLE entry point: appeal / program change / withdraw) ---
// Scholars-specific request CREATION (the Person Search "flag" dialog routes here for Scholars applicants
// instead of the shared Honors `changeRequested`, FR-22). requestType "APPEAL" additionally requires
// subProgramId — the Scholars program being appealed, chosen in the dialog (a denied applicant may have been
// denied from several). Admin or student-employee; case number optional for admins. Supersedes the former
// createScholarsAppeal.
export const createScholarsRequest = `
    mutation createScholarsRequest($applicantEmployeeId: String!, $term: String, $requestType: String!, $reason: String!, $caseNumber: String, $subProgramId: String) {
        createScholarsRequest(applicantEmployeeId: $applicantEmployeeId, term: $term, requestType: $requestType, reason: $reason, caseNumber: $caseNumber, subProgramId: $subProgramId) {
            status
            successDetails
            errorDetails
        }
    }
`;

// has503 — Scholars-specific application details (separate authorizer from Honors). Mirrors the
// getApplicationDetails selection set so the shared ScholarsApplicant sections render unchanged.
export const getScholarsApplicationDetails = `
    query getScholarsApplicationDetails($applicantEmployeeId: String!, $programId: String!, $term: String!) {
        getScholarsApplicationDetails(applicantEmployeeId: $applicantEmployeeId, programId: $programId, term: $term) {
            details {
                appBar {
                    applicantEmployeeId
                    campusId
                    firstName
                    is3rdReviewer
                    lastName
                    programId
                    programName
                    priority
                    dateOfBirth
                }
                history {
                    reviewNumber
                    assignTime
                    submitTime
                    name
                    isNotifying
                    isAdminRecommendation
                    recommendation
                    reviewType
                    recommendationReason
                    administrativeNotes
                    rubricValues
                }
                demographics {
                    academicPlan
                    academicPlanDescription
                    academicProgram
                    academicProgramDescription
                    admitType
                    countryOfCitizenship
                    earlyAction
                    firstGen
                    homeCountry
                    lowSocioeconomic
                    regularDecision
                    residency
                    rural
                    stage
                    term
                    termLabel
                    priority
                }
                administratorDetails
                highSchool {
                    highSchoolName
                    highSchoolLocation
                    highSchoolGraduationDate
                    ceebCode
                    highSchoolGPA
                    classStandingSize
                    classRankPercentileType
                    bestTest {
                        comments
                        score
                    }
                }
                collegePrepUnits {
                    differenceForeignLanguage
                    differenceMath
                    differenceNaturalScience
                    earnedForeignLanguage
                    earnedMath
                    earnedNaturalScience
                    minimumForeignLanguage
                    minimumMath
                    minimumNaturalScience
                }
                testScores {
                    scores {
                        act {
                            composite
                            date
                            english
                            math
                            reading
                            science
                        }
                        sat {
                            date
                            evidenceBased
                            mathSection
                            readingTest
                            total
                        }
                    }
                    bestTest {
                        comments
                        score
                    }
                }
                resumeLaterRubricValues
                resumeLaterAdminNotes {
                    label
                    section
                }
            }
            status {
                assignable
                currentRecommendation
                decision
                priority
                isApplicationReviewed
                isApplicationFinalized
                isApplicationCancelled
                canAdminOverride
                isAssignedReviewer
                isSubmittedReviewer
                reviewRecommendation
                isScholarsInProgressOwner
            }
            configuration {
                rubric
                administrativeNotes
                recommendationOptions
                adminOverrideOptions
                adminOverrideReasons
                assignableReviewers {
                    id
                    name
                }
                unassignableReviewers {
                    id
                    name
                }
            }
        }
    }
`;

// --- Preferencing simulation (hidden admin tool) ----------------------------
// has489 — OPEN Scholars programs for the current cycle term (lean projection). Drives the ranking pool for
// the preferencing simulator. Term is resolved server-side; returns only active/open programs.
// NOTE: the simulator submits preferences via adjustScholarsPreferences (admin on-behalf; tolerates both
// SCHOLARS_AWAITING_PREFERENCES and SCHOLARS_PREFERENCING_REQUESTED), not submitScholarsPreferences.
export const getOpenScholarsPrograms = `
    query getOpenScholarsPrograms {
        getOpenScholarsPrograms {
            subProgramId
            term
            label
        }
    }
`;
